/**
 * Cabeçalho e navegação dos ecrãs internos.
 *
 * Aqui a navegação é filtrada pelo papel: um funcionário não vê sequer o
 * separador das contas. Isso é conveniência, não segurança — quem forçar o
 * endereço leva 403 do servidor, que é quem decide.
 */

import { useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Brand from '../common/Brand';
import SynapraFooter from '../common/SynapraFooter';
import { IconBell, IconReceipt, IconChart, IconGrid, IconTable, IconUsers, IconLogout } from '../ui/icons';
import './shell.css';

import { useSessao } from '../../auth/SessionContext';
import type { TenantRole } from '../../types/domain';

interface Separador {
  path: string;
  label: string;
  /** Vazio = visível a qualquer papel. */
  papeis?: TenantRole[];
}

const GESTAO: TenantRole[] = ['owner', 'manager', 'superuser'];
const SALA: TenantRole[] = ['owner', 'manager', 'waiter', 'superuser'];

const SEPARADORES: Separador[] = [
  { path: '/staff-mobile', label: 'Atendimento', papeis: SALA },
  { path: '/historico', label: 'Histórico', papeis: GESTAO },
  { path: '/manager', label: 'Vendas', papeis: GESTAO },
  { path: '/admin/menu', label: 'Menu', papeis: GESTAO },
  { path: '/admin/mesas', label: 'Mesas', papeis: GESTAO },
  { path: '/admin/contas', label: 'Contas', papeis: GESTAO },
];

const NOME_DO_PAPEL: Record<TenantRole, string> = {
  owner: 'Proprietário',
  manager: 'Gerente',
  waiter: 'Funcionário',
  kitchen: 'Perfil antigo de cozinha',
  superuser: 'Plataforma',
};

export const AppShell = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const { sessao, terminarSessao } = useSessao();

  const [leaving, setLeaving] = useState(false);
  const papel = sessao?.tenants[0]?.role;
  const visiveis = SEPARADORES.filter(
    (s) => !s.papeis || (papel && s.papeis.includes(papel)),
  );

  const icons = [IconBell, IconReceipt, IconChart, IconGrid, IconTable, IconUsers];
  return (
    <div className={'surface app-shell' + (pathname === '/staff-mobile' ? ' surface--staff' : '')}>
      <a className="skip-link" href="#workspace-content">Saltar para o conteúdo</a>
      <header className="app-header">
        <div className="shell-brand"><Brand subtitle="Espaço da equipa" /><span className="shell-mobile-venue">{sessao?.tenants[0]?.name ?? 'Bamidi'}</span></div>
        <div className="shell-venue"><span>Restaurante</span><strong>{sessao?.tenants[0]?.name ?? 'Bamidi'}</strong></div>
        {visiveis.length > 1 && <nav className="nav" aria-label="Secções">
          {visiveis.map(separador => {
            const Icon = icons[SEPARADORES.indexOf(separador)]!;
            return <NavLink key={separador.path} to={separador.path} className={({ isActive }) => isActive ? 'active' : ''}>
              <Icon tamanho={20} /><span>{separador.label}</span>
            </NavLink>;
          })}
        </nav>}
        <div className="session">
          <span className="session-avatar" aria-hidden="true">{sessao?.user.slice(0, 1).toUpperCase()}</span>
          <span className="session-identity"><span className="session-name">{sessao?.user}</span><span className="session-role">{papel ? NOME_DO_PAPEL[papel] : 'Sem acesso'}</span></span>
          <button type="button" className="session-exit" disabled={leaving} aria-label="Sair" title="Sair" onClick={async () => {
            if (leaving) return;
            setLeaving(true);
            try { await terminarSessao(); } finally { setLeaving(false); }
          }}><IconLogout tamanho={19} /></button>
        </div>
      </header>
      <div id="workspace-content" className="app-content" tabIndex={-1}>
        {children}
        <SynapraFooter />
      </div>
    </div>
  );
};
